/* oxlint-disable confluence-feature-gating/no-preconditioning -- The master must short-circuit child gates in code to prevent child exposure in control. */
import React, { memo, useSyncExternalStore } from 'react';

import { CommentAgentSessionPublisher } from '@confluence/agent-mention/entry-points/CommentAgentSessionPublisher.tsx';
import {
	expValEquals,
	UNSAFE_expValEqualsNoExposure,
	isExperimentEnabled,
} from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';

import { ContentCommentAgentReplies } from './ContentCommentAgentReplies.tsx';
import {
	isCommentAgentSessionContentType,
	isCommentAgentSessionScopeValid,
} from './content-comment-agent-session-service.ts';
import type { ContentCommentAgentSessionService } from './content-comment-agent-session-service.ts';

type Props = {
	contentId: string;
	contentType: string;
	registry: ContentCommentAgentSessionService;
};

function EnabledPublisher({ contentId, contentType, registry }: Props) {
	const hasListeners = useSyncExternalStore(
		registry.subscribeToDemand,
		registry.hasListeners,
		registry.hasListeners,
	);
	return (
		<TransparentErrorBoundary
			key={registry.scopeKey}
			attribution={Attribution.COMMENTS}
			onError={() => {
				registry.replies.dispose();
				registry.publish([]);
			}}
		>
			{UNSAFE_expValEqualsNoExposure(
				'platform_editor_inline_agentic_comments',
				'isEnabled',
				true,
			) && <ContentCommentAgentReplies contentId={contentId} registry={registry} />}
			<CommentAgentSessionPublisher
				contentId={contentId}
				contentType={contentType}
				pollingEnabled={hasListeners}
				onChange={registry.publish}
			/>
		</TransparentErrorBoundary>
	);
}

export const ContentCommentAgentSessionPublisher: React.NamedExoticComponent<Props> = memo(
	function ContentCommentAgentSessionPublisher(props: Props): React.JSX.Element | null {
		const { cloudId, userId } = useSessionData();
		if (
			!isCommentAgentSessionContentType(props.contentType) ||
			!isCommentAgentSessionScopeValid(props.contentId, cloudId, userId)
		) {
			return null;
		}
		if (
			!(
				isExperimentEnabled('canvas_agentic_experiences_master_exp') &&
				fg('canvas-rovo-comment-sessions')
			)
		)
			return null;
		if (!expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)) return null;
		return <EnabledPublisher {...props} />;
	},
);
